import React, { useState, useEffect } from 'react';
import {
  FaShieldAlt,
  FaBolt,
  FaHeart,
  FaStar,
  FaSkull,
  FaArrowRight,
  FaCrosshairs,
  FaPlusCircle,
} from 'react-icons/fa';
import { scheduleAbilityGainSoundForBatch } from './playAbilityGainSound';

// Custom empty circle component for Shield ability
const EmptyCircle = ({ size = 18, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <circle cx="12" cy="12" r="10" />
  </svg>
);

/**
 * Large centered ability symbol burst when abilities are gained or units with abilities appear.
 */
export const AbilitySymbolAnimation = ({
  ability,
  onComplete,
  delay = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    // Start animation after delay
    const timer = setTimeout(() => {
      setIsVisible(true);

      // Auto-remove after animation completes
      const removeTimer = setTimeout(() => {
        setIsVisible(false);
        setHasCompleted(true);
        if (onComplete) {
          // Call onComplete immediately, don't wait for fade out
          onComplete();
        }
      }, 1500); // Show for 1.5 seconds

      return () => clearTimeout(removeTimer);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, onComplete]);

  // Get ability icon component
  const getAbilityIcon = (abilityName) => {
    const iconMap = {
      'Protect': FaShieldAlt,
      'Shield': EmptyCircle,
      'Blitz': FaBolt,
      'Recover': FaHeart,
      'Redeem': FaStar,
      'Fell': FaSkull,
      'Penetrate': FaArrowRight,
      'Sniper': FaCrosshairs,
      'MendWounds': FaPlusCircle,
    };
    return iconMap[abilityName] || null;
  };

  // Get the icon component for this ability
  const IconComponent = getAbilityIcon(ability);
  
  // Don't render at all if ability is missing, icon is null, animation completed, or not yet visible
  if (!ability || !IconComponent || hasCompleted || !isVisible) return null;

  const positionStyles = {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  const animationKeyframes = `
    @keyframes abilityCenterLargeBurst {
      0% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.5);
      }
      18% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1.08);
      }
      28% {
        transform: translate(-50%, -50%) scale(1);
      }
      78% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
      100% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.92);
      }
    }
  `;

  const animationName = 'abilityCenterLargeBurst';

  const iconSize = 88;

  return (
    <>
      <div
        style={{
          position: 'absolute',
          ...positionStyles,
          zIndex: 25,
          pointerEvents: 'none',
          color: 'white',
          filter: 'drop-shadow(0 0 18px rgba(255, 220, 120, 0.85)) drop-shadow(2px 4px 8px rgba(0,0,0,0.75))',
          animation: `${animationName} 1.5s ease-in-out`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconComponent size={iconSize} />
      </div>
      <style>{animationKeyframes}</style>
    </>
  );
};

const ABILITY_ICON_NAMES = new Set([
  'Protect',
  'Shield',
  'Blitz',
  'Recover',
  'Redeem',
  'Fell',
  'Penetrate',
  'Sniper',
  'MendWounds',
]);

/** Persists across OrderDesign remounts (e.g. hand carousel index-based keys). */
const handIntroCompletedByInstance = new Set();
/** instanceId -> abilities present when hand intro completed (survives carousel remounts). */
const handIntroAbilitiesByInstance = new Map();
/** `${instanceId}::${ability}` — each ability symbol animates at most once per instance. */
const abilityRevealCompletedKeys = new Set();

function abilityRevealKey(instanceId, ability) {
  return `${String(instanceId)}::${ability}`;
}

function markHandAbilitiesRevealed(instanceId, abilities) {
  for (const ability of abilities) {
    abilityRevealCompletedKeys.add(abilityRevealKey(instanceId, ability));
  }
}

function recordHandIntroCompleted(instanceId, abilitiesWithSymbols) {
  handIntroCompletedByInstance.add(instanceId);
  handIntroAbilitiesByInstance.set(instanceId, [...abilitiesWithSymbols]);
  markHandAbilitiesRevealed(instanceId, abilitiesWithSymbols);
}

function scheduleSoundForAnimationBatch(abilitiesToShow) {
  if (!Array.isArray(abilitiesToShow) || abilitiesToShow.length === 0) {
    return;
  }
  // SFX at grant time — visual symbol animations keep their own stagger delay.
  scheduleAbilityGainSoundForBatch(
    abilitiesToShow.map((entry) => ({
      ability: entry.ability,
      delayMs: 0,
    })),
    abilitiesToShow.map((entry) => entry.id).join('|')
  );
}

function commitAnimatingAbilities(setAnimatingAbilities, abilitiesToShow, append = false) {
  if (abilitiesToShow.length === 0) {
    return;
  }
  scheduleSoundForAnimationBatch(abilitiesToShow);
  if (append) {
    setAnimatingAbilities((prev) => [...prev, ...abilitiesToShow]);
  } else {
    setAnimatingAbilities(abilitiesToShow);
  }
}

function filterAbilitiesWithSymbols(abilities) {
  return abilities.filter((ability) => ABILITY_ICON_NAMES.has(ability));
}

/**
 * Hook to track ability changes and trigger animations
 */
export const useAbilityAnimations = (order, minimal, zoneName) => {
  const [animatingAbilities, setAnimatingAbilities] = useState([]);
  const previousAbilitiesRef = React.useRef(new Map()); // Track abilities per instanceId
  const previousLocationRef = React.useRef(new Map()); // Track location per instanceId
  const hasAppearedRef = React.useRef(new Map()); // Track appearance per instanceId
  const previousInstanceIdRef = React.useRef(null);
  const previousMinimalRef = React.useRef(null);
  const lastAnimationTriggerRef = React.useRef(new Map()); // Track last animation trigger to prevent duplicates
  const currentlyAnimatingRef = React.useRef(new Set()); // Track which ability+instanceId combinations are currently animating
  const previousHasShieldRef = React.useRef(new Map()); // Track hasShield per instanceId (re-shield grants)

  useEffect(() => {
    // Only process when minimal is true (don't reset on hover/unhover)
    if (!order || !minimal) {
      previousMinimalRef.current = minimal;
      return;
    }

    // Don't trigger animations just because minimal changed from false to true (hover/unhover)
    // Only process if this is the first time seeing minimal=true, or if order/abilities/location actually changed
    const wasMinimalFalse = previousMinimalRef.current === false;
    previousMinimalRef.current = minimal;

    if (!order.instanceId) {
      return;
    }
    const currentInstanceId = String(order.instanceId);

    // Reset tracking if this is a different unit
    if (previousInstanceIdRef.current && previousInstanceIdRef.current !== currentInstanceId) {
      // Clear animations for previous unit
      setAnimatingAbilities([]);
      // Clear currently animating set for previous unit
      const previousInstanceId = previousInstanceIdRef.current;
      currentlyAnimatingRef.current.forEach(key => {
        if (key.startsWith(`${previousInstanceId}-`)) {
          currentlyAnimatingRef.current.delete(key);
        }
      });
    }
    previousInstanceIdRef.current = currentInstanceId;

    const currentAbilities = getAbilities(order);
    const abilitiesWithSymbols = filterAbilitiesWithSymbols(currentAbilities);

    // Determine location: zoneName prop indicates battlefield, null/undefined indicates hand
    const currentLocation = zoneName || order.zone || 'hand';
    const isHandLocation = currentLocation === 'hand';

    // Carousel remount (e.g. strategem removed from hand shifts index-based keys): hook refs
    // reset but module-level hand intro may already have run — restore reveal state so
    // unrelated battle actions do not replay ability fly-ins.
    if (
      isHandLocation &&
      handIntroCompletedByInstance.has(currentInstanceId) &&
      !previousAbilitiesRef.current.has(currentInstanceId)
    ) {
      const introAbilities =
        handIntroAbilitiesByInstance.get(currentInstanceId) || abilitiesWithSymbols;
      markHandAbilitiesRevealed(currentInstanceId, introAbilities);
      previousAbilitiesRef.current.set(currentInstanceId, [...introAbilities]);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      hasAppearedRef.current.set(currentInstanceId, true);
    }

    const previousAbilities = filterAbilitiesWithSymbols(
      previousAbilitiesRef.current.get(currentInstanceId) || []
    );
    const previousLocation = previousLocationRef.current.get(currentInstanceId);
    const hasAppeared =
      hasAppearedRef.current.get(currentInstanceId) ||
      handIntroCompletedByInstance.has(currentInstanceId);

    const locationChanged =
      previousLocation !== undefined && previousLocation !== currentLocation;
    const isHandFirstIntro =
      isHandLocation &&
      abilitiesWithSymbols.length > 0 &&
      !handIntroCompletedByInstance.has(currentInstanceId);
    const isFirstAppearance =
      isHandFirstIntro ||
      (!isHandLocation &&
        previousLocation === undefined &&
        abilitiesWithSymbols.length > 0);

    const newAbilities = abilitiesWithSymbols.filter(
      (ability) =>
        !previousAbilities.includes(ability) &&
        !abilityRevealCompletedKeys.has(abilityRevealKey(currentInstanceId, ability))
    );

    const currentHasShield = order.hasShield === true;
    const previousHasShield = previousHasShieldRef.current.get(currentInstanceId);
    const shieldReActivated =
      hasAppeared &&
      currentHasShield &&
      previousHasShield !== true &&
      abilitiesWithSymbols.includes('Shield');

    const grantAbilities = [...newAbilities];
    if (shieldReActivated && !grantAbilities.includes('Shield')) {
      grantAbilities.push('Shield');
    }

    // Skip if we're just coming back from hover (minimal was false, now true) and nothing actually changed
    if (wasMinimalFalse && previousLocation === currentLocation && 
        JSON.stringify(previousAbilities.sort()) === JSON.stringify(abilitiesWithSymbols.sort())) {
      // Just update refs without triggering animations
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      previousHasShieldRef.current.set(currentInstanceId, currentHasShield);
      return;
    }

    // Create a unique trigger key to prevent duplicate animations
    const sortedAbilities = [...abilitiesWithSymbols].sort(); // Sort copy for consistent comparison
    const sortedPreviousAbilities = [...previousAbilities].sort();
    const abilitiesKey = JSON.stringify(sortedAbilities);
    const triggerKey = `${currentInstanceId}-${currentLocation}-${abilitiesKey}`;
    const lastTrigger = lastAnimationTriggerRef.current.get(triggerKey);
    const timeSinceLastTrigger = lastTrigger ? Date.now() - lastTrigger : Infinity;
    
    // Check if we've already processed this exact state (immediate check)
    // Only consider it "already processed" if:
    // - Location hasn't changed AND
    // - Abilities haven't changed AND  
    // - We've already appeared (not first appearance)
    const alreadyProcessed = hasAppeared && 
                            previousLocation === currentLocation && 
                            JSON.stringify(sortedPreviousAbilities) === abilitiesKey;
    
    // Only trigger if:
    // 1. We haven't triggered for this exact state in the last 500ms (prevents double triggers from React Strict Mode)
    // 2. We haven't already processed this exact state (prevents duplicate triggers in same render)
    const shouldTrigger = timeSinceLastTrigger > 500 && !alreadyProcessed;

    // Helper to check if an ability is currently animating
    const isCurrentlyAnimating = (abilityName) => {
      const animatingKey = `${currentInstanceId}-${abilityName}`;
      return currentlyAnimatingRef.current.has(animatingKey);
    };

    const markAbilityRevealCompleted = (abilityName) => {
      abilityRevealCompletedKeys.add(abilityRevealKey(currentInstanceId, abilityName));
    };

    // Helper to mark ability as animating
    const markAsAnimating = (abilityName) => {
      const animatingKey = `${currentInstanceId}-${abilityName}`;
      currentlyAnimatingRef.current.add(animatingKey);
      markAbilityRevealCompleted(abilityName);
    };

    const abilitiesNotYetRevealed = (abilities, allowShieldReactivation = false) =>
      abilities.filter((ability) => {
        if (ability === 'Shield' && allowShieldReactivation) {
          return !isCurrentlyAnimating(ability);
        }
        return (
          !abilityRevealCompletedKeys.has(abilityRevealKey(currentInstanceId, ability)) &&
          !isCurrentlyAnimating(ability)
        );
      });

    const buildAnimationEntries = (abilities, optionsForEntry = {}) =>
      abilities.map((ability, index) => {
        markAsAnimating(ability);
        return {
          ability,
          instanceId: currentInstanceId,
          id: `${currentInstanceId}-${ability}-${Date.now()}-${index}`,
          delay: index * 200,
          ...optionsForEntry,
        };
      });

    // Trigger animations for:
    // 1. Unit first appears in hand (once per instanceId, survives carousel remounts)
    // 2. Unit first appears on the battlefield
    // 3. Abilities newly gained during play (only symbols not yet revealed)
    if (isFirstAppearance && shouldTrigger) {
      hasAppearedRef.current.set(currentInstanceId, true);
      if (isHandLocation) {
        recordHandIntroCompleted(currentInstanceId, abilitiesWithSymbols);
      }
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      lastAnimationTriggerRef.current.set(triggerKey, Date.now());

      const abilitiesToShow = buildAnimationEntries(abilitiesNotYetRevealed(abilitiesWithSymbols));

      if (abilitiesToShow.length > 0) {
        commitAnimatingAbilities(setAnimatingAbilities, abilitiesToShow);
      }
    } else if (
      locationChanged &&
      !isHandLocation &&
      abilitiesWithSymbols.length > 0 &&
      hasAppeared &&
      shouldTrigger
    ) {
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      lastAnimationTriggerRef.current.set(triggerKey, Date.now());

      const abilitiesToShow = buildAnimationEntries(abilitiesNotYetRevealed(abilitiesWithSymbols));

      if (abilitiesToShow.length > 0) {
        commitAnimatingAbilities(setAnimatingAbilities, abilitiesToShow);
      }
    } else if (grantAbilities.length > 0 && hasAppeared && shouldTrigger) {
      // Abilities gained during play, or active shield restored (Front Line Sentinel re-shield)
      const grantKey = shieldReActivated && newAbilities.length === 0
        ? `${currentInstanceId}-${currentLocation}-shield-on`
        : `${currentInstanceId}-${currentLocation}-new-${JSON.stringify(newAbilities)}`;
      const lastGrantTrigger = lastAnimationTriggerRef.current.get(grantKey);
      const timeSinceGrant = lastGrantTrigger ? Date.now() - lastGrantTrigger : Infinity;

      if (timeSinceGrant > 500) {
        // ✅ CRITICAL: Update refs IMMEDIATELY to prevent duplicate triggers
        previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
        previousLocationRef.current.set(currentInstanceId, currentLocation);
        lastAnimationTriggerRef.current.set(grantKey, Date.now());

        const abilitiesToShow = buildAnimationEntries(
          abilitiesNotYetRevealed(grantAbilities, shieldReActivated)
        );

        if (abilitiesToShow.length > 0) {
          commitAnimatingAbilities(setAnimatingAbilities, abilitiesToShow, true);
        }
      }
    } else {
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      if (isHandLocation && abilitiesWithSymbols.length > 0) {
        recordHandIntroCompleted(currentInstanceId, abilitiesWithSymbols);
        hasAppearedRef.current.set(currentInstanceId, true);
      } else if (!isHandLocation) {
        // Battlefield units with no starting abilities (e.g. Toxic Coilbeast) must still
        // count as "appeared" so Momentum Shift / other first-ability grants animate + play SFX.
        hasAppearedRef.current.set(currentInstanceId, true);
      }
    }

    previousHasShieldRef.current.set(currentInstanceId, currentHasShield);
  }, [
    order,
    minimal,
    zoneName,
    order?.abilities,
    order?.instanceAbilities,
    order?.hasInstanceModifications,
    order?.hasShield,
    order?.zone,
    order?.instanceId,
  ]);

  const removeAnimation = (id) => {
    setAnimatingAbilities(prev => {
      // Find the animation being removed to get its ability name and instanceId
      const animToRemove = prev.find(anim => anim.id === id);
      if (animToRemove) {
        // Remove from currently animating set
        const animatingKey = `${animToRemove.instanceId || 'unknown'}-${animToRemove.ability}`;
        currentlyAnimatingRef.current.delete(animatingKey);
      }
      return prev.filter(anim => anim.id !== id);
    });
  };

  return { animatingAbilities, removeAnimation };
};

// Helper function to get abilities (same as in OrderDesign)
function getAbilities(order) {
  if (!order) return [];
  // ✅ FIX: Check instanceAbilities first if hasInstanceModifications is true
  // This ensures dynamically added abilities (e.g., Blitz from Exolot Finisher) are included
  if (order.hasInstanceModifications && Array.isArray(order.instanceAbilities)) {
    return order.instanceAbilities;
  }
  if (Array.isArray(order.abilities)) return order.abilities;
  if (typeof order.ability === "string") return [order.ability];
  if (Array.isArray(order.abilitiesList)) return order.abilitiesList;
  return [];
}

export default AbilitySymbolAnimation;

